import { ReactNode } from "react";
// 组件
import { DeleteOutlined } from "@ant-design/icons";
import { ProButton, ProImage, ProTooltip } from "../../antd";
import Images from "../../Images";
import SkuInfoList from "../../SkuInfoList";
import CopyText from "../../CopyText";
import RouteLink from "../../RouteLink";
// 方法
import { getColumnValue } from "../../utils/dataUtils";
import { renderEnumNames } from "../../utils/renderUtils";
import { filterEmptyValue } from "../../utils/utils";
import { setColumnTitle } from "../../SuperTable/utils";
// 常量
import { IEnum } from "../../PageLayout/context/LayoutContext";
import { ColumnType } from "antd/lib/table";
import skuInfoListConfig from "../../constants/skuInfoListConfig";
import { EdiTableColumnsType, EdiTableDispatchType, EdiTableProps, IEdiTablePayload, SORT_DATA_INDEX } from "../interface";
import { FormInstance } from "antd/lib/form";
import { TABLE_COLUMN_EMPTY } from "../../constants/constant";

function useColumns(
  props: EdiTableProps,
  dispatchEdiTable: (type: EdiTableDispatchType, payload?: IEdiTablePayload) => void,
  form: FormInstance<any>,
  tableElement: HTMLDivElement | null,
  enumData?: IEnum,
) {
  const {
    columns: outColumns, disabled, operation, operationTitle, operationWidth, hideAdd, addIcon, isSort, isMove,
    sortColumnType, moveColumnType, sortFieldName,
    onBeforeAdd, onBeforeDelete, disabledRowOperation,
  } = props;
  /** 添加行 */
  const handleAdd = () => {
    const addDataSource = onBeforeAdd ? onBeforeAdd({}) : {};
    if (!addDataSource) return;
    dispatchEdiTable('addRow', { addItem: addDataSource, });
  };
  /** 删除行 */
  const handleDelete = (record: any) => {
    const deleteDataSource = onBeforeDelete ? onBeforeDelete(record) : {};
    if (!deleteDataSource) return;
    dispatchEdiTable('deleteRow', { deleteKeys: [record.key], });
  }
  const baseColumnsProps: ColumnType<any> = {
    ellipsis: {
      showTitle: false,
    },
  };
  function formatColumns(
    columnsProps: EdiTableColumnsType[],
    isChildren: boolean = false,
  ) {
    // @ts-ignore
    const columns: EdiTableColumnsType[] = columnsProps.map(outColumn => {
      const {
        editable,
        required,
        title,
        dataIndex,
        enumCode,
        width,
        renderType,
        children,
        tooltip,
        align,
        disabled: columnDisabled,
        skuInfoListProps,
        routeLinkProps,
        ...restOutColumn
      } = outColumn;
      if (editable) {
        return {
          dataIndex,
          width,
          align,
          title: setColumnTitle(title, tooltip, align, required),
          onCell: (record: any, rowIndex: number) => {
            // 表单默认值
            let initialValue: any;
            // 单元格dataIndex类型为string
            if (typeof dataIndex === 'string') {
              initialValue = record[dataIndex];
            }
            // 单元格表单类型为SuperSelectTable且单元格dataIndex类型为array
            if (
              restOutColumn.formItemType === 'SuperSelectTable' &&
              Array.isArray(dataIndex)
            ) {
              initialValue = record[dataIndex[0]];
            }
            // 根据当前行的数据无匹配的默认值则取列表配置中设置的默认值
            if (initialValue === undefined && restOutColumn.initialValue !== undefined) {
              initialValue = restOutColumn.initialValue;
            }
            return {
              record,
              editable,
              tableElement,
              rowKey: record.key,
              form,
              rowIndex,
              align,
              formItemProps: {
                enumCode,
                required,
                label: title,
                name: dataIndex,
                disabled: disabled || (typeof columnDisabled === 'function' ? columnDisabled(record) : columnDisabled),
                ...restOutColumn,
                initialValue,
              },
            };
          },
        };
      }
      if (enumCode && enumData) {
        const enumItem = enumData[`${enumCode}`];
        return enumItem
          ? {
              ...baseColumnsProps,
              ...outColumn,
              title: setColumnTitle(title, tooltip, align, required),
              render: (_: any, record: any) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (columnValue === undefined || columnValue === '' || columnValue === null) {
                  return TABLE_COLUMN_EMPTY;
                }
                const enumValues = renderEnumNames(columnValue, enumItem.items);
                return (
                  <ProTooltip placement="topLeft" title={enumValues}>
                    {enumValues}
                  </ProTooltip>
                );
              },
            }
          : { ...baseColumnsProps, ...outColumn };
      }
      if (Array.isArray(dataIndex)) {
        return {
          ...baseColumnsProps,
          ...outColumn,
          title: setColumnTitle(title, tooltip, align, required),
          render: (_: any, record: any) => {
            return (
              <>
                {dataIndex.map(item => {
                  const columnValue = getColumnValue(record, item);
                  if (renderType === 'copy') {
                    return <CopyText text={columnValue} isBlock key={item}/>;
                  }
                  return (
                    <ProTooltip
                      key={item}
                      placement="topLeft"
                      title={columnValue}
                      mouseEnterDelay={1}
                    >
                      <div className="ant-table-cell-ellipsis">
                        {columnValue || TABLE_COLUMN_EMPTY}
                      </div>
                    </ProTooltip>
                  );
                })}
              </>
            );
          },
        };
      }
      switch (renderType) {
        case 'skuInfoList':
          const skuInfoListWidth = Number(width || 600);
          return {
            ...baseColumnsProps,
            ...outColumn,
            title: setColumnTitle(title, tooltip, align, required),
            children: children ? formatColumns(children, true) : undefined,
            width: skuInfoListWidth + 10,
            render: (_: string, record: any) => {
              const columnValue = getColumnValue(record, dataIndex);
              return (
                <SkuInfoList
                  data={columnValue}
                  width={skuInfoListWidth}
                  {...skuInfoListConfig}
                  {...skuInfoListProps}
                />
              );
            },
          };
        case 'image':
          return {
            ...baseColumnsProps,
            className: 'table-image-column',
            ...outColumn,
            title: setColumnTitle(title, tooltip, align, required),
            render: (_: any, record: any) => {
              const columnValue = getColumnValue(record, dataIndex);
              return <ProImage className="table-column-image" src={columnValue} />;
            },
          };
        case 'images':
          return {
            ...outColumn,
            width: outColumn.width || 280,
            title: setColumnTitle(title, tooltip, align, required),
            render: (_: any, record: any) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (Array.isArray(columnValue)) return <Images images={columnValue} />;
              return null;
            },
          };
        case 'boolean':
          return {
            ...baseColumnsProps,
            ...outColumn,
            title: setColumnTitle(title, tooltip, align, required),
            render: (_: any, record: any) => {
              const columnValue = getColumnValue(record, dataIndex);
              return columnValue ? '是' : '否';
            },
          };
        case 'copy':
          return {
            ...baseColumnsProps,
            ...outColumn,
            title: setColumnTitle(title, tooltip, align, required),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              return <CopyText text={columnValue} align={align} />;
            },
          };
        case 'routeLink':
          return {
            ...baseColumnsProps,
            ...outColumn,
            title: setColumnTitle(title, tooltip, align, required),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              const routeLinkQuery = routeLinkProps?.query;
              const routeLinkState = routeLinkProps?.state;
              return (
                <RouteLink
                  text={columnValue}
                  {...routeLinkProps}
                  query={typeof routeLinkQuery === 'function' ? routeLinkQuery(record) : routeLinkQuery}
                  state={typeof routeLinkState === 'function' ? routeLinkState(record) : routeLinkState}
                />
              );
            },
          };
      }
      return {
        ...baseColumnsProps,
        ...outColumn,
        title: setColumnTitle(title, tooltip, align, required),
        children: children ? formatColumns(children, true) : undefined,
        render: (_: any, record: any, key: number) => {
          const columnValue = getColumnValue(record, dataIndex);
          if (columnValue === undefined || columnValue === '' || columnValue === null) {
            return outColumn.render
              ? outColumn.render(columnValue, record, key)
              : TABLE_COLUMN_EMPTY;
          }
          if (typeof columnValue === 'string') {
            // @ts-ignore
            const columnHTML: ReactNode = outColumn.render ? outColumn.render(columnValue, record, key) : columnValue;
            return (
              <ProTooltip placement="topLeft" title={columnValue}>
                {columnHTML}
              </ProTooltip>
            );
          }
          return outColumn.render ? outColumn.render(columnValue, record, key) : filterEmptyValue(columnValue);
        },
      };
    });
    if (isChildren) return columns;
    // 操作列配置
    if (operation !== false && !disabled) {
      const operationColumns: ColumnType<any> = operation || {
        fixed: 'right',
        width: operationWidth,
        align: 'center',
        title: hideAdd ? null : (operationTitle ? operationTitle : (
          <ProButton type="primary" size="small" onClick={handleAdd}>
            {addIcon}
          </ProButton>
        )),
        render: (_: any, record: any) => {
          let disabledOperation: boolean = false;
          if (disabledRowOperation) {
            disabledOperation = disabledRowOperation(record);
          }
          if (disabledOperation) return TABLE_COLUMN_EMPTY;
          return (
            <DeleteOutlined
              className='text-danger'
              onClick={() => handleDelete(record)}
              title="删除"
            />
          );
        },
      }
      columns.push(operationColumns);
    }
    // 排序列
    if (!isMove && isSort && sortColumnType) {
      const outSortColumnType = sortColumnType === true ? {} : sortColumnType;
      columns.unshift({
        title: '序号',
        width: 80,
        dataIndex: sortFieldName,
        fixed: 'left',
        render: (_: any, __: any, key: number) => {
          return key + 1;
        },
        ...outSortColumnType,
      });
    }
    // 拖动排序列
    if (isMove && !disabled) {
      columns.unshift({
        dataIndex: SORT_DATA_INDEX,
        width: 80,
        fixed: 'left',
        ...moveColumnType,
      });
    }
    return columns;
  }
  return formatColumns(outColumns || [], false);
};

export default useColumns;
